// Composables
import axios from 'axios'
import { h, resolveComponent, nextTick } from 'vue'
import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocation,
} from 'vue-router'

import ArchivesLayout from '@/archives/layouts/ArchivesLayout.vue'
import Archives from '@/archives/views/Archives.vue'
import ArchivesAdd from '@/archives/views/ArchivesAdd.vue'
import CerteuropeSign from '@/certeurope-sign/CerteuropeSign.vue'
import DocumentViewer from '@/common/document-viewer/DocumentViewer.vue'
import { AUTH_DATA_COOKIE_KEY } from '@/common/utils/login'
import { QUOTE_REQUEST_TOPICS } from '@/common/utils/quoteRequestTopics'
import ExpiredLink from '@/error/views/ExpiredLink.vue'
import NotFound from '@/error/views/NotFound.vue'
import SwitchAccount from '@/error/views/SwitchAccount.vue'
import ImAuth from '@/imanage/views/ImAuth.vue'
import ActivateAccount from '@/login/views/ActivateAccount.vue'
import AuthByToken from '@/login/views/AuthByToken.vue'
import DownloadFromToken from '@/login/views/DownloadFromToken.vue'
import ForgotPassword from '@/login/views/ForgotPassword.vue'
import Logout from '@/login/views/Logout.vue'
import ResetPassword from '@/login/views/ResetPassword.vue'
import SamlAcs from '@/login/views/SamlAcs.vue'
import SignIn from '@/login/views/SignIn.vue'
import TwoFactor from '@/login/views/TwoFactor.vue'
import Payment from '@/payment/Payment.vue'
import PaymentCancel from '@/payment/PaymentCancel.vue'
import PaymentSuccess from '@/payment/PaymentSuccess.vue'
import i18n, { AvailableLocale } from '@/plugins/i18n'
import Profile from '@/profile/layouts/Profile.vue'
import EmailAlias from '@/profile/views/EmailAlias.vue'
import IdentityChecking from '@/profile/views/IdentityChecking.vue'
import Password from '@/profile/views/Password.vue'
import PersonalInformation from '@/profile/views/PersonalInformation.vue'
import Settings from '@/profile/views/Settings.vue'
import Bibles from '@/project/bibles/Bibles.vue'
import Documents from '@/project/documents/Documents.vue'
import SigningChecklistLayout from '@/project/documents/layouts/SigningChecklistLayout.vue'
import QuestionAnswerLayout from '@/project/layouts/QuestionAnswerLayout.vue'
import Members from '@/project/members/Members.vue'
import SignedDocuments from '@/project/signed-documents/SignedDocuments.vue'
import EnvelopeTemplateLayout from '@/project/signing-checklist/envelope-templates/layouts/EnvelopeTemplateLayout.vue'
import Checklist from '@/project/views/Checklist.vue'
import EnvelopeTemplateAdd from '@/project/views/EnvelopeTemplateAdd.vue'
import Logs from '@/project/views/Logs.vue'
import Project from '@/project/views/Project.vue'
import ProjectSettings from '@/project/views/ProjectSettings.vue'
import QuestionAnswerForm from '@/project/views/QuestionAnswerForm.vue'
import QuestionAnswerItem from '@/project/views/QuestionAnswerItem.vue'
import QuestionAnswerList from '@/project/views/QuestionAnswerList.vue'
import SigningChecklist from '@/project/views/SigningChecklist.vue'
import SigningChecklistAdd from '@/project/views/SigningChecklistAdd.vue'
import SigningChecklistOrder from '@/project/views/SigningChecklistOrder.vue'
import SigningView from '@/project/views/SigningView.vue'
import WatermarkView from '@/project/views/WatermarkView.vue'
import Projects from '@/projects/views/Projects.vue'
import LandingPage from '@/sign/views/LandingPage.vue'
import store from '@/store'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { SET_CURRENT_ROOM_ROUTE_DECIDED } from '@/store/modules/room/mutation_types'
import { SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG } from '@/store/modules/subscription/mutation_types'
import Subscription from '@/subscriptions/views/Subscription.vue'
import SubscriptionBilling from '@/subscriptions/views/SubscriptionBilling.vue'
import SubscriptionClosedProjects from '@/subscriptions/views/SubscriptionClosedProjects.vue'
import SubscriptionConsumption from '@/subscriptions/views/SubscriptionConsumption.vue'
import SubscriptionProjects from '@/subscriptions/views/SubscriptionProjects.vue'
import SubscriptionSignaturesConsumption from '@/subscriptions/views/SubscriptionSignaturesConsumption.vue'
import SubscriptionSubscribers from '@/subscriptions/views/SubscriptionSubscribers.vue'
import SubscriptionTeams from '@/subscriptions/views/SubscriptionTeams.vue'
import AppStatus from '@/views/AppStatus.vue'
import DocumentSearchPage from '@/views/DocumentSearchPage.vue'
import WopiPage from '@/wopi/views/WopiPage.vue'

/** BEGIN Routes names */
export const ROOMS_ROUTE_NAME = 'rooms-listing'
// Virtual route. There is nothing there and it only renders its children
export const NO_AUTH_ROUTE_NAME = 'no-auth'
export const SIGN_IN_ROUTE_NAME = 'sign-in'
export const LOGOUT_ROUTE_NAME = 'log-out'
export const FORGOT_PASSWORD_ROUTE_NAME = 'forgot-password'
export const ACTIVATE_ACCOUNT_ROUTE_NAME = 'activate-account'
export const RESET_PASSWORD_ROUTE_NAME = 'reset-password'
export const IM_AUTH_ROUTE_NAME = 'im-auth'
export const SAML_ACS_ROUTE_NAME = 'saml-acs'
export const TWO_FACTOR_ROUTE_NAME = 'two-factor'
export const GLOBAL_SEARCH_ROUTE_NAME = 'global-search'
export const PROJECT_SEARCH_ROUTE_NAME = 'project-search'

/** Subscription */
export const SUBSCRIPTION_ROUTE_NAME = 'subscription'
export const SUBSCRIPTION_SIGNATURES_CONSUMPTION_ROUTE_NAME = 'subscription-signatures-consumption'
export const SUBSCRIPTION_CONSUMPTION_ROUTE_NAME = 'subscription-consumption'
export const SUBSCRIPTION_BILLING_ROUTE_NAME = 'subscription-billing'
export const SUBSCRIPTION_SUBSCRIBERS_ROUTE_NAME = 'subscription-subscribers'
export const SUBSCRIPTION_TEAMS_ROUTE_NAME = 'subscription-teams'
export const SUBSCRIPTION_PROJECTS_ROUTE_NAME = 'subscription-projects'
export const SUBSCRIPTION_CLOSED_PROJECTS_ROUTE_NAME = 'subscription-closed-projects'
/** Profile */
export const PROFILE_ROUTE_NAME = 'profile'
export const PROFILE_PERSONAL_INFORMATION_ROUTE_NAME = `${PROFILE_ROUTE_NAME}-personal-information`
export const PROFILE_EMAIL_ALIAS_ROUTE_NAME = `${PROFILE_ROUTE_NAME}-email-alias`
export const PROFILE_IDENTITY_CHECKING_ROUTE_NAME = `${PROFILE_ROUTE_NAME}-identity-checking`
export const PROFILE_PASSWORD_ROUTE_NAME = `${PROFILE_ROUTE_NAME}-password`
export const PROFILE_SETTINGS_ROUTE_NAME = `${PROFILE_ROUTE_NAME}-settings`
/** Room */
export const ROOM_ROUTE_NAME = 'room'
export const ROOM_SETTINGS_ROUTE_NAME = `${ROOM_ROUTE_NAME}-settings`
export const ROOM_MEMBERS_ROUTE_NAME = `${ROOM_ROUTE_NAME}-members`
export const ROOM_DOCUMENTS_ROUTE_NAME = `${ROOM_ROUTE_NAME}-documents`
export const ROOM_DOCUMENT_VIEWER_ROUTE_NAME = `${ROOMS_ROUTE_NAME}-document-viewer`
export const ROOM_WATERMARK_VIEW_ROUTE_NAME = `${ROOMS_ROUTE_NAME}-watermark`
export const ROOM_CHECKLIST_ROUTE_NAME = `${ROOM_ROUTE_NAME}-checklist`
export const ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME = `${ROOM_ROUTE_NAME}-checklist-deletion-validation`
export const ROOM_SIGNED_DOCUMENTS_ROUTE_NAME = `${ROOM_ROUTE_NAME}-signed-documents`
export const ROOM_BIBLES_ROUTE_NAME = `${ROOM_ROUTE_NAME}-bibles`
export const ROOM_LOGS_ROUTE_NAME = `${ROOM_ROUTE_NAME}-logs`
/** Folder */
export const FOLDER_ROUTE_NAME = 'folder'
export const FOLDER_CHILD_ROUTE_NAME = 'folder-child'
/** Signing-checklist */
export const SIGNING_CHECKLIST_ROUTE_NAME = 'signing-checklist'
export const SIGNING_CHECKLIST_INDEX_ROUTE_NAME = `${SIGNING_CHECKLIST_ROUTE_NAME}-index`
export const SIGNING_CHECKLIST_ADD_ROUTE_NAME = `${SIGNING_CHECKLIST_ROUTE_NAME}-add`
export const SIGNING_CHECKLIST_ORDER_ROUTE_NAME = `${SIGNING_CHECKLIST_ROUTE_NAME}-order`
/** Question Answer */
export const QUESTION_ANSWER_ROUTE_NAME = 'question-answer'
export const QUESTION_ANSWER_LIST_ROUTE_NAME = 'question-answer-list'
export const QUESTION_ANSWER_ADD_ROUTE_NAME = 'question-answer-add'
export const QUESTION_ANSWER_EDIT_ROUTE_NAME = 'question-answer-edit'
export const QUESTION_ANSWER_ITEM_ROUTE_NAME = 'question-answer-item'
/** Signature */
export const SIGNING_VIEW_ROUTE_NAME = 'signing-view'
// Separate signing view because we cannot be inside the room at that time
export const NDA_SIGNING_VIEW_ROUTE_NAME = 'nda-signing-view'
/** Archives */
export const ARCHIVES_ROUTE_NAME = 'archives'
export const ARCHIVES_ADD_ROUTE_NAME = `${ARCHIVES_ROUTE_NAME}-add`
/** Certeurope sign **/
export const CERTEUROPE_SIGN_ROUTE_NAME = 'certeurope-sign'
/** Error */
export const NOT_FOUND_ROUTE_NAME = 'not-found'
/** Switch account when already logged with an other account */
export const SWITCH_ACCOUNT_ROUTE_NAME = 'switch-account'
/** Common sign */
export const SIGN_LANDING_PAGE_ROUTE_NAME = 'sign-landing-page'
export const SIMPLE_SIGNATURE_ROUTE_NAME = 'simple-signature'
export const NOTIFICATIONS_PREFERENCES_ROUTE_NAME = 'notifications-preferences'
/** Download routes */
export const SIGNED_DOCUMENT_DOWNLOAD_ROUTE_NAME = 'signed-document-download'
export const EXPIRED_LINK_ROUTE_NAME = 'expired-link'
export const PDF_FILE_DOWNLOAD_ROUTE_NAME = 'pdf-file-download'
export const PDF_FULL_FILE_DOWNLOAD_ROUTE_NAME = 'pdf-full-file-download'
export const PDF_ALL_FILES_DOWNLOAD_ROUTE_NAME = 'pdf-all-files-download'
export const ROOM_PDF_FILE_DOWNLOAD_ROUTE_NAME = 'room-pdf-file-download'
export const ROOM_FILE_DOWNLOAD_ROUTE_NAME = 'room-file-download'
/** Payment **/
export const PAYMENT_ROUTE_NAME = 'payment'
export const PAYMENT_SUCCESS_ROUTE_NAME = 'payment-success'
export const PAYMENT_CANCEL_ROUTE_NAME = 'payment-cancel'
export const PAYMENT_ROUTE_NAMES = [PAYMENT_ROUTE_NAME, PAYMENT_SUCCESS_ROUTE_NAME, PAYMENT_CANCEL_ROUTE_NAME]
/** Envelope templates */
export const ENVELOPE_TEMPLATE_ADD_ROUTE_NAME = 'envelope-template-add'
export const ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME = 'envelope-template-update'

export const LOGIN_ARRAY_ROUTE_NAME = [SIGN_IN_ROUTE_NAME, ACTIVATE_ACCOUNT_ROUTE_NAME, RESET_PASSWORD_ROUTE_NAME]

export const HOME_LAYOUT_ROUTE_NAMES = [ROOMS_ROUTE_NAME, FOLDER_ROUTE_NAME, FOLDER_CHILD_ROUTE_NAME, GLOBAL_SEARCH_ROUTE_NAME]

/** WOPI */
export const WOPI_PAGE_ROUTE_NAME = 'wopi-page'

export const TOKEN_SCOPES = Object.freeze({
  SIMPLE_SIGNATURE: 'simple-signature',
  NOTIFICATIONS_PREFERENCES: 'notifications-preferences',
  FULL_ACCESS: 'full-access',
})

/**
 * Available room types
 * @readonly
 * @enum {string}
 */
// TODO : Convert to true enum during typescript file rewrite
export const ROOM_ROUTE_TYPES = Object.freeze({
  DATAROOM: 'datarooms',
  PROJECT: 'projects',
  // This type represents a type that we don't know yet and
  // thus needs to be computed after getting the room infos.
  // Used to redirect from legacy routes.
  UNDEFINED: '_',
})

const localeLanguage = i18n.global.locale.value
const mnemoRegexp = '[0-9A-Za-z]{13}'
const roomTypesRegexp = `${ROOM_ROUTE_TYPES.DATAROOM}|${ROOM_ROUTE_TYPES.PROJECT}|${ROOM_ROUTE_TYPES.UNDEFINED}`

const routes = [
  // LEGACY ROUTES
  // Used for routes that needs redirecting and can't support aliasing
  {
    path: '/connexion',
    redirect: `/${localeLanguage}/sign-in`,
  },
  {
    path: '/en/login',
    redirect: '/en/sign-in',
  },
  {
    path: '/password/init/:token',
    redirect: (to: RouteLocation) => {
      return `/${localeLanguage}/password/init/${to.params['token']}`
    },
  },
  {
    path: '/password/reset/:token',
    redirect: (to: RouteLocation) => {
      return `/${localeLanguage}/password/reset/${to.params['token']}`
    },
  },
  {
    path: '/payment/stripe/:token',
    redirect: (to: RouteLocation) => {
      return `/${localeLanguage}/payment/${to.params['token']}`
    },
  },
  {
    path: '/pdf_sign/file/:token',
    redirect: (to: RouteLocation) => {
      return `/pdfSign/file/${to.params['token']}`
    },
  },
  {
    path: '/pdf_sign/fullfile/:token',
    redirect: (to: RouteLocation) => {
      return `/pdfSign/fullFile/${to.params['token']}`
    },
  },
  {
    path: '/pdf_sign/allfiles/:token',
    redirect: (to: RouteLocation) => {
      return `/pdfSign/allFiles/${to.params['token']}`
    },
    caseSensitive: true,
  },
  // The back-end adds the type to the download mail routes with mnemos but not the API
  // The route will try to download based on its URL, so we strip the type from it.
  {
    path: `/room/:roomType/:mnemo(${mnemoRegexp})/downloads/pdf/:fileId`,
    redirect: (to: RouteLocation) => {
      return `/room/${to.params['mnemo']}/downloads/pdf/${to.params['fileId']}`
    },
  },
  /**
   Old path with optional type. Old email path that is to be removed later on the back-end
   @since 1.40.0
   */
  {
    path: `/:roomType?/:mnemo(${mnemoRegexp})/getPdf/:fileId`,
    redirect: (to: RouteLocation) => {
      return `/room/${to.params['mnemo']}/downloads/pdf/${to.params['fileId']}`
    },
  },
  /**
   Old path with optional type. Old email path that is to be removed later on the back-end
   @since 1.40.0
   */
  {
    path: `/:roomType?/:mnemo(${mnemoRegexp})/getFile/:fileId`,
    redirect: (to: RouteLocation) => {
      return `/room/${to.params['mnemo']}/downloads/file/${to.params['fileId']}`
    },
  },
  {
    path: `/${ROOM_ROUTE_TYPES.PROJECT}/:mnemo(${mnemoRegexp})/getSignedDocuments/:token`,
    redirect: (to: RouteLocation) => {
      return `/${to.params['mnemo']}/getSignedDocuments/${to.params['token']}`
    },
  },
  {
    path: `/:roomType?/:mnemo(${mnemoRegexp})/getBible/:id`,
    redirect: (to: RouteLocation) => {
      return {
        path: `${to.params['roomType'] || '_'}/${to.params['mnemo']}/bibles`,
        query: {
          idBible: to.params['id'],
        },
      }
    },
  },
  {
    path: `/:locale/:roomType(${roomTypesRegexp})/:mnemo(${mnemoRegexp})/bibles`,
    redirect: (to: RouteLocation) => {
      return `/${to.params['roomType']}/${to.params['mnemo']}/bibles`
    },
  },
  {
    // Older path. No mnemo, can't go to sign but should still redirect to activate account
    path: '/password/init/:token/sign/:envelopeId',
    redirect: (to: RouteLocation) => {
      return `/${localeLanguage}/password/init/${to.params['token']}`
    },
  },
  {
    path: `/password/init/:token/:mnemo(${mnemoRegexp})/sign/:envelopeId`,
    redirect: (to: RouteLocation) => {
      return {
        path: `/${localeLanguage}/password/init/${to.params['token']}`,
        query: {
          envelopeIdToSign: to.params['envelopeId'],
          mnemo: to.params['mnemo'],
        },
      }
    },
  },
  {
    path: `/:locale/password/init/:token/:mnemo(${mnemoRegexp})/sign/:envelopeId`,
    redirect: (to: RouteLocation) => {
      return {
        path: `/${to.params['locale']}/password/init/${to.params['token']}`,
        query: {
          envelopeIdToSign: to.params['envelopeId'],
          mnemo: to.params['mnemo'],
        },
      }
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/signature/:envelopeId/getSigningView/`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.PROJECT}/${to.params['mnemo']}/${to.params['envelopeId']}/signing-view`
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: `/${ROOM_ROUTE_TYPES.PROJECT}/:mnemo(${mnemoRegexp})/signature/:envelopeId/getSigningView/`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.PROJECT}/${to.params['mnemo']}/${to.params['envelopeId']}/signing-view`
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/signature/:envelopeId/getSigningView/:token`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.PROJECT}/${to.params['mnemo']}/signature/${to.params['envelopeId']}/getSigningView/${to.params['token']}`
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/signature/:envelopeId/sign`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.PROJECT}/${to.params['mnemo']}/signature/${to.params['envelopeId']}/sign`
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/getSigningView/:token`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.PROJECT}/${to.params['mnemo']}/getSigningView/${to.params['token']}`
    },
  },
  {
    path: '/logout',
    name: LOGOUT_ROUTE_NAME,
    props: (route: RouteLocation) => ({ ...route.query }),
    component: Logout,
  },
  {
    path: '/imAuth',
    name: IM_AUTH_ROUTE_NAME,
    component: ImAuth,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/saml-acs',
    name: SAML_ACS_ROUTE_NAME,
    component: SamlAcs,
  },
  {
    path: '/',
    name: ROOMS_ROUTE_NAME,
    component: Projects,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('common.titles.projects'),
    },
  },
  {
    path: '/profile',
    alias: '/my-profile',
    name: PROFILE_ROUTE_NAME,
    component: Profile,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('common.titles.profil.main'),
    },
    redirect: { name: PROFILE_PERSONAL_INFORMATION_ROUTE_NAME },
    children: [
      {
        path: 'personal-information',
        name: PROFILE_PERSONAL_INFORMATION_ROUTE_NAME,
        component: PersonalInformation,
        meta: {
          title: i18n.global.t('common.titles.profil.personal'),
        },
      },
      {
        path: 'email-alias/:token?',
        name: PROFILE_EMAIL_ALIAS_ROUTE_NAME,
        component: EmailAlias,
        meta: {
          title: i18n.global.t('common.titles.profil.emailAlias'),
        },
      },
      {
        path: 'identity-checking',
        name: PROFILE_IDENTITY_CHECKING_ROUTE_NAME,
        component: IdentityChecking,
        meta: {
          title: i18n.global.t('common.titles.profil.idcheck'),
        },
      },
      {
        path: 'password',
        name: PROFILE_PASSWORD_ROUTE_NAME,
        component: Password,
        meta: {
          title: i18n.global.t('common.titles.profil.password'),
        },
      },
      {
        path: 'settings',
        name: PROFILE_SETTINGS_ROUTE_NAME,
        component: Settings,
        meta: {
          title: i18n.global.t('common.titles.profil.settings'),
          scopeAllowed: TOKEN_SCOPES.NOTIFICATIONS_PREFERENCES,
        },
      },
    ],
  },
  {
    path: '/subscription',
    name: SUBSCRIPTION_ROUTE_NAME,
    component: Subscription,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('common.titles.subscription.main'),
    },
    redirect: { name: SUBSCRIPTION_BILLING_ROUTE_NAME },
    children: [
      {
        path: 'signatures-consumption',
        name: SUBSCRIPTION_SIGNATURES_CONSUMPTION_ROUTE_NAME,
        component: SubscriptionSignaturesConsumption,
        meta: {
          title: i18n.global.t('common.titles.subscription.signaturesConsumption'),
        },
      },
      {
        path: 'consumption',
        name: SUBSCRIPTION_CONSUMPTION_ROUTE_NAME,
        component: SubscriptionConsumption,
        meta: {
          title: i18n.global.t('common.titles.subscription.consumption'),
        },
      },
      {
        path: 'billing',
        name: SUBSCRIPTION_BILLING_ROUTE_NAME,
        component: SubscriptionBilling,
        meta: {
          title: i18n.global.t('common.titles.subscription.billing'),
        },
      },
      {
        path: 'subscribers',
        name: SUBSCRIPTION_SUBSCRIBERS_ROUTE_NAME,
        component: SubscriptionSubscribers,
        meta: {
          title: i18n.global.t('common.titles.subscription.subscribers'),
        },
      },
      {
        path: 'teams',
        name: SUBSCRIPTION_TEAMS_ROUTE_NAME,
        component: SubscriptionTeams,
        meta: {
          title: i18n.global.t('common.titles.subscription.teams'),
        },
      },
      {
        path: 'projects',
        name: SUBSCRIPTION_PROJECTS_ROUTE_NAME,
        component: SubscriptionProjects,
        meta: {
          title: i18n.global.t('common.titles.subscription.projects'),
        },
      },
      {
        path: 'closed-projects',
        name: SUBSCRIPTION_CLOSED_PROJECTS_ROUTE_NAME,
        component: SubscriptionClosedProjects,
        meta: {
          title: i18n.global.t('common.titles.subscription.closedProjects'),
        },
      },
    ],
  },
  {
    path: '/archives',
    component: ArchivesLayout,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('common.titles.archives'),
    },
    children: [
      {
        path: '',
        name: ARCHIVES_ROUTE_NAME,
        component: Archives,
      },
      {
        path: 'add',
        name: ARCHIVES_ADD_ROUTE_NAME,
        component: ArchivesAdd,
        props: true,
      },
    ],
  },
  {
    path: '/folder/:folderId',
    name: FOLDER_ROUTE_NAME,
    component: Projects,
    meta: {
      requiresAuth: true,
    },
    props: true,
    children: [
      {
        path: ':childId?',
        name: FOLDER_CHILD_ROUTE_NAME,
        component: Projects,
        props: true,
      },
    ],
  },
  {
    path: `/${ROOM_ROUTE_TYPES.DATAROOM}/:mnemo(${mnemoRegexp})/nda/signing-view/`,
    props: (route: RouteLocation) => ({ ...route.params, isNda: true }),
    name: NDA_SIGNING_VIEW_ROUTE_NAME,
    component: SigningView,
    meta: {
      requiresAuth: true,
    },
  },
  // IMPORTANT: Put all routes at the top level before the project_route
  // Because of the legacy alias that's dynamic, we need all static routes
  // That also match to be before it to avoid problems at init
  {
    path: `/:roomType(${roomTypesRegexp})/:mnemo(${mnemoRegexp})`,
    name: ROOM_ROUTE_NAME,
    component: Project,
    meta: {
      requiresAuth: true,
    },
    props: true,
    children: [
      {
        path: 'settings',
        name: ROOM_SETTINGS_ROUTE_NAME,
        props: true,
        component: ProjectSettings,
      },
      {
        path: 'members',
        name: ROOM_MEMBERS_ROUTE_NAME,
        props: true,
        component: Members,
      },
      {
        path: 'documents',
        name: ROOM_DOCUMENTS_ROUTE_NAME,
        props: true,
        component: Documents,
      },
      {
        path: 'watermark-view/:fileId',
        alias: 'watermark/:fileId',
        name: ROOM_WATERMARK_VIEW_ROUTE_NAME,
        props: true,
        component: WatermarkView,
      },
      {
        path: 'checklist',
        alias: 'agenda',
        name: ROOM_CHECKLIST_ROUTE_NAME,
        props: true,
        component: Checklist,
      },
      {
        path: 'checklist/validate/deletion',
        name: ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME,
        props: true,
        component: Checklist,
      },
      {
        path: 'signing-checklist',
        alias: 'signing-guide',
        props: true,
        component: SigningChecklistLayout,
        name: SIGNING_CHECKLIST_ROUTE_NAME,
        redirect: { name: SIGNING_CHECKLIST_INDEX_ROUTE_NAME },
        children: [
          {
            path: '',
            props: true,
            name: SIGNING_CHECKLIST_INDEX_ROUTE_NAME,
            component: SigningChecklist,
          },
          {
            path: 'add',
            props: true,
            name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
            component: SigningChecklistAdd,
          },
          {
            path: 'order',
            props: true,
            name: SIGNING_CHECKLIST_ORDER_ROUTE_NAME,
            component: SigningChecklistOrder,
          },
        ],
      },
      {
        path: ':envelopeId/signing-view/:token?',
        props: (route: RouteLocation) => ({ ...route.params }),
        name: SIGNING_VIEW_ROUTE_NAME,
        component: SigningView,
        meta: {
          scopeAllowed: TOKEN_SCOPES.SIMPLE_SIGNATURE,
        },
      },
      {
        path: 'signed-documents',
        alias: 'my-signatures',
        name: ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
        props: true,
        component: SignedDocuments,
      },
      {
        path: 'bibles',
        name: ROOM_BIBLES_ROUTE_NAME,
        props: true,
        component: Bibles,
      },
      {
        path: 'qa',
        component: QuestionAnswerLayout,
        name: QUESTION_ANSWER_ROUTE_NAME,
        redirect: { name: QUESTION_ANSWER_LIST_ROUTE_NAME },
        children: [
          {
            path: '',
            props: true,
            name: QUESTION_ANSWER_LIST_ROUTE_NAME,
            component: QuestionAnswerList,
          },
          {
            path: 'add',
            props: true,
            name: QUESTION_ANSWER_ADD_ROUTE_NAME,
            component: QuestionAnswerForm,
          },
          {
            path: 'edit/:questionId',
            props: (route: RouteLocation) => ({ ...route.query, ...route.params, isEditMode: true }),
            name: QUESTION_ANSWER_EDIT_ROUTE_NAME,
            component: QuestionAnswerForm,
          },
          {
            path: ':questionId',
            props: true,
            name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
            component: QuestionAnswerItem,
          },
        ],
      },
      {
        path: 'logs',
        name: ROOM_LOGS_ROUTE_NAME,
        props: true,
        component: Logs,
      },
      {
        path: 'envelope-template',
        component: EnvelopeTemplateLayout,
        children: [
          {
            path: 'add',
            name: ENVELOPE_TEMPLATE_ADD_ROUTE_NAME,
            component: EnvelopeTemplateAdd,
            props: { formMode: 'creation' },
          },
          {
            path: 'update',
            name: ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME,
            component: EnvelopeTemplateAdd,
            props: { formMode: 'edition' },
          },
        ],
      },
      {
        path: 'search',
        name: PROJECT_SEARCH_ROUTE_NAME,
        component: DocumentSearchPage,
        props: true,
        meta: {
          title: i18n.global.t('common.titles.search'),
        },
      },
    ],
  },
  {
    path: '/search',
    name: GLOBAL_SEARCH_ROUTE_NAME,
    component: DocumentSearchPage,
    props: true,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('common.titles.search'),
    },
  },
  {
    path: `/${ROOM_ROUTE_TYPES.PROJECT}/:mnemo(${mnemoRegexp})/signature/:envelopeId/sign`,
    name: CERTEUROPE_SIGN_ROUTE_NAME,
    props: true,
    component: CerteuropeSign,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: `/signature/landing/:mnemo(${mnemoRegexp})`,
    alias: `/signature/landing/${ROOM_ROUTE_TYPES.PROJECT}/:mnemo(${mnemoRegexp})`,
    name: SIGN_LANDING_PAGE_ROUTE_NAME,
    props: (route: RouteLocation) => ({ ...route.query, ...route.params }),
    component: LandingPage,
    meta: {
      requiresAuth: false,
    },
  },
  // NO-AUTH PATHS
  // They need a locale param to help set a display language.
  // The parent route template only renders the actual route.
  {
    path: '/:locale([A-Za-z]{2})',
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    name: NO_AUTH_ROUTE_NAME,
    props: true,
    beforeEnter: (
      to: RouteLocation,
      from: RouteLocation,
      next: NavigationGuardNext,
    ) => {
      // The payment routes are an exception that requires the locale and can be used logged in or not logged in
      if ((axios.defaults.headers.common.Authorization || window.$cookies.get(AUTH_DATA_COOKIE_KEY)) && !PAYMENT_ROUTE_NAMES.includes(to.name as string)) {
        if (to.name === ACTIVATE_ACCOUNT_ROUTE_NAME) {
          return next({
            name: LOGOUT_ROUTE_NAME,
            query: {
              redirectPath: to.path,
            },
          })
        }
        return next('/')
      }
      const supportedLocales = i18n.global.availableLocales
      const locale = (to.params['locale'] as string)?.substring(0, 2) as AvailableLocale
      if (!supportedLocales.includes(locale)) {
        i18n.global.locale.value = 'en'
        to.params['locale'] = 'en'
        return next({ ...to, params: { ...to.params, locale: 'en' } } as RouteLocation)
      } else if (i18n.global.locale.value !== locale) {
        i18n.global.locale.value = locale
      }
      return next()
    },
    meta: {
      requiresAuth: false,
    },
    children: [
      {
        path: 'sign-in',
        name: SIGN_IN_ROUTE_NAME,
        props: (route: RouteLocation) => ({ ...route.query }),
        component: SignIn,
        meta: {
          title: i18n.global.t('common.titles.auth.main'),
        },
      },
      {
        path: 'password/init/:token',
        name: ACTIVATE_ACCOUNT_ROUTE_NAME,
        component: ActivateAccount,
        props: true,
        meta: {
          requiresAuth: false,
          title: i18n.global.t('common.titles.auth.activate'),
        },
      },
      {
        path: 'password/reset',
        name: FORGOT_PASSWORD_ROUTE_NAME,
        component: ForgotPassword,
        meta: {
          requiresAuth: false,
          title: i18n.global.t('common.titles.auth.forgot'),
        },
      },
      {
        path: 'password/reset/:token',
        name: RESET_PASSWORD_ROUTE_NAME,
        component: ResetPassword,
        props: true,
        meta: {
          requiresAuth: false,
          title: i18n.global.t('common.titles.auth.reset'),
        },
      },
      {
        path: 'payment/success',
        name: PAYMENT_SUCCESS_ROUTE_NAME,
        component: PaymentSuccess,
        props: true,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'payment/cancel',
        name: PAYMENT_CANCEL_ROUTE_NAME,
        component: PaymentCancel,
        props: true,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'payment/:token',
        name: PAYMENT_ROUTE_NAME,
        component: Payment,
        props: true,
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: '/two-factor',
    name: TWO_FACTOR_ROUTE_NAME,
    component: TwoFactor,
    props: (route: RouteLocation) => ({ ...route.query, ...route.params }),
    meta: {
      requiresAuth: false,
      title: i18n.global.t('common.titles.auth.dfa'),
    },
  },
  {
    path: `/${ROOM_ROUTE_TYPES.PROJECT}/:mnemo(${mnemoRegexp})/signature/:envelopeId/getSigningView/:token`,
    name: SIMPLE_SIGNATURE_ROUTE_NAME,
    component: AuthByToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/:token/notifications',
    name: NOTIFICATIONS_PREFERENCES_ROUTE_NAME,
    component: AuthByToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: `/room/:mnemo(${mnemoRegexp})/downloads/pdf/:fileId`,
    name: ROOM_PDF_FILE_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: `/room/:mnemo(${mnemoRegexp})/downloads/file/:fileId`,
    name: ROOM_FILE_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: `/room/:mnemo(${mnemoRegexp})/wopi/file/:fileId/:mode`,
    name: WOPI_PAGE_ROUTE_NAME,
    component: WopiPage,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/getSignedDocuments/:token`,
    name: SIGNED_DOCUMENT_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/documents/:fileId`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.UNDEFINED}/${to.params['mnemo']}/documents/${to.params['fileId']}`
    },
  },
  {
    path: `/:roomType(${roomTypesRegexp})/:mnemo(${mnemoRegexp})/documents/:fileId`,
    name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
    props: true,
    component: DocumentViewer,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/expired-link',
    name: EXPIRED_LINK_ROUTE_NAME,
    component: ExpiredLink,
    props: (route: RouteLocation) => ({
      expirationDate: route.query['expirationDate'],
      expirationHour: route.query['expirationHour'],
    }),
    meta: {
      title: i18n.global.t('Error.views.expiredLink.metaTitle'),
      requiresAuth: false,
    },
  },
  {
    path: '/pdfSign/allFiles/:token',
    name: PDF_ALL_FILES_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/pdfSign/fullFile/:token',
    name: PDF_FULL_FILE_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/pdfSign/file/:token',
    name: PDF_FILE_DOWNLOAD_ROUTE_NAME,
    component: DownloadFromToken,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  // Variant from an email, no specific page accessed
  {
    path: `/:mnemo(${mnemoRegexp})`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.UNDEFINED}/${to.params['mnemo']}/`
    },
  },
  {
    path: `/:mnemo(${mnemoRegexp})/*`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.UNDEFINED}/${to.params['mnemo']}/*`
    },
  },
  {
    path: `/project/:mnemo(${mnemoRegexp})/*`,
    redirect: (to: RouteLocation) => {
      return `/${ROOM_ROUTE_TYPES.UNDEFINED}/${to.params['mnemo']}/*`
    },
  },
  {
    path: '/switch-account/:locale/:customMessage',
    name: SWITCH_ACCOUNT_ROUTE_NAME,
    component: SwitchAccount,
    props: (route: RouteLocation) => ({ ...route.query, ...route.params }),
    meta: {
      requiresAuth: false,
    },
  },
  // Application status
  // This is a public page that shows the status of the application
  {
    path: '/status',
    meta: {
      title: 'Application Status',
    },
    component: AppStatus,
  },
  {
    path: '/:pathMatch(.*)?',
    name: NOT_FOUND_ROUTE_NAME,
    component: NotFound,
    props: (route: RouteLocation) => ({ ...route.query, ...route.params }),
    meta: {
      requiresAuth: false,
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

/**
 * Check if the router is ready and has already completed its initial navigation.
 *
 * Since `router.isReady() returns a promise, this is able to guess without waiting for a promise.
 * Especially useful in scenarios where we're working _in the router_ and can't wait for it to be ready
 * @returns {boolean}
 */
export const isRouterReady = () => router.currentRoute.value.matched.length !== 0

/**
 * Get the current route even if the router is not ready yet.
 * @returns {RouteLocationNormalized} The current route
 */
export const getCurrentRouteSafe = () =>
  isRouterReady()
    ? router.currentRoute.value
    : router.resolve(router.options.history.location)

router.beforeEach((to, from, next) => {
  try {
    // See V2-1636. This is the hash used from Pendo to know we want to show the
    // dataroom contact form in-app
    if (to.hash === '#dataroom-info') {
      // DO THE THING
      // Reset the hash
      location.hash = ''
      store.commit('subscription/' + SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG, {
        defaultProjectType: QUOTE_REQUEST_TOPICS.DATAROOM,
        isOpen: true,
      })
    } else {
      store.commit('global/' + GlobalMutationTypes.SET_ROUTER_HISTORY, from)
    }
  } catch (e) {
    console.error(e)
  }
  next()
})

router.afterEach(async (to) => {
  // If we are navigating to a room route, we set the route decided to true _after_ navigating to it
  // This avoids a re-render of the current component when the route has not actually changed yet
  // See V2-5514
  if (to.matched.find((route) => route.name === ROOM_ROUTE_NAME)) {
    store.commit('room/' + SET_CURRENT_ROOM_ROUTE_DECIDED, true)
  }
  try {
    nextTick(async () => {
      const title = (to.meta as unknown as any).title
      if (title) {
        document.title = title + ' - Closd'
      }
    })
  } catch (e) {
    console.error(e)
    console.info('Couldn\'t tell HappyFox a navigation occurred')
  }
})

export default router
